import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import {
  ChildDevelopmentListResponse,
  ChildDevelopmentMilestoneSchema,
  ChildDevelopmentProgressRecordsResponse,
  NurseryChildResponse,
} from "chaild-api/lib";
import LocalDataService from "@/service/LocalDataService";
import ApiChildDevelopment, {
  ChildDevelopmentUpdateRequestBody,
} from "@/api/ApiChildDevelopment";
import ApiChildren from "@/api/ApiChildren";

const cleanMilestone = (milestone: ChildDevelopmentMilestoneSchema) => {
  if (milestone.prevYear === "done") {
    milestone.apr = null;
    milestone.may = null;
    milestone.june = null;
    milestone.july = null;
    milestone.aug = null;
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
    return milestone;
  }

  if (milestone.apr === "done") {
    milestone.may = null;
    milestone.june = null;
    milestone.july = null;
    milestone.aug = null;
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.may === "done") {
    milestone.june = null;
    milestone.july = null;
    milestone.aug = null;
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.june === "done") {
    milestone.july = null;
    milestone.aug = null;
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.july === "done") {
    milestone.aug = null;
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.aug === "done") {
    milestone.sept = null;
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.sept === "done") {
    milestone.oct = null;
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.oct === "done") {
    milestone.nov = null;
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.nov === "done") {
    milestone.dec = null;
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.dec === "done") {
    milestone.jan = null;
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.jan === "done") {
    milestone.feb = null;
    milestone.mar = null;
  }

  if (milestone.feb === "done") {
    milestone.mar = null;
  }

  return milestone;
};

export interface ChildDevelopmentChildState {
  developmentRecords: ChildDevelopmentListResponse;
  child: NurseryChildResponse | null;
  progressRecords: ChildDevelopmentProgressRecordsResponse;
}

@(Module({ dynamic: true, store, name: "child-development-child", namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class ChildDevelopmentChildModule
  extends VuexModule
  implements ChildDevelopmentChildState
{
  developmentRecords: ChildDevelopmentListResponse = [];
  child: NurseryChildResponse | null = null;
  progressRecords: ChildDevelopmentProgressRecordsResponse = [];

  @Action
  async listRecords({
    childId,
    businessYear,
  }: {
    childId: number;
    businessYear: number;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const response = await ApiChildDevelopment.getChildDevelopments({
        nurseryId,
        businessYear,
        childId,
      });
      if (response) {
        this.setRecords(response);
      }
    }
  }

  @Mutation
  setRecords(records: ChildDevelopmentListResponse) {
    this.developmentRecords = records;
  }

  @Action
  async getChild({ childId }: { childId: number }) {
    const response = await ApiChildren.getChild(childId);
    this.setChild(response);
  }

  @Mutation
  setChild(child: NurseryChildResponse | null) {
    this.child = child;
  }

  @Action
  async updateRecords({
    childId,
    businessYear,
  }: {
    childId: number;
    businessYear: number;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const requestBody: ChildDevelopmentUpdateRequestBody[] =
        this.developmentRecords.map((r) => {
          const milestone = cleanMilestone(r.milestone);
          return {
            childDevelopmentId: r.childDevelopmentId,
            milestone: milestone,
          };
        });
      await ApiChildDevelopment.updateChildDevelopments({
        childId,
        businessYear,
        nurseryId,
        body: requestBody,
      });
    }
  }

  @Action
  async listProgressRecords({
    childId,
    businessYear,
  }: {
    childId: number;
    businessYear: number;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const response = await ApiChildDevelopment.getProgressRecords({
        nurseryId,
        businessYear,
        childId,
      });
      if (response) {
        this.setProgressRecords(response);
      }
    }
  }

  @Mutation
  setProgressRecords(records: ChildDevelopmentProgressRecordsResponse) {
    this.progressRecords = records;
  }

  @Action
  async updateProgressRecord({
    childId,
    businessYear,
    month,
    content,
  }: {
    childId: number;
    businessYear: number;
    month: number;
    content: string | null;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const response = await ApiChildDevelopment.updateProgressRecord({
        nurseryId,
        businessYear,
        childId,
        month,
        content,
      });
      if (response) {
        this.updateProgressRecordInList(response);
      }
    }
  }

  @Mutation
  updateProgressRecordInList(record: { month: number; content: string | null; updatedAt: string | null }) {
    const index = this.progressRecords.findIndex((r) => r.month === record.month);
    if (index !== -1) {
      this.progressRecords.splice(index, 1, record);
    }
  }
}

export const childDevelopmentChildModule = getModule(
  ChildDevelopmentChildModule
);
